import React, { useCallback, useEffect, useState } from 'react';
import SecteursZonesMap from './SecteursZonesMap';
import secteursService from '../../services/secteursService';
import {
  formatCa,
  geocodeAdresse,
  isPointInAnyZone,
} from '../../utils/secteursMapUtils';
import { AppAlert, AppSpinner } from '../ui';

const SOURCE_LABELS = {
  HUBSPOT: { label: 'HubSpot', className: 'bg-primary-50 text-primary-600' },
  'SIRENE-LOCAL': { label: 'SIRENE local', className: 'bg-success-50 text-success-600' },
  ADRESSE: { label: 'Adresse', className: 'bg-danger-50 text-danger-600' },
};

function normalizeHubspotItem(item) {
  return {
    id: `hubspot-${item.siret || item.record_id || item.nom}`,
    source: 'HUBSPOT',
    nom: item.nom_raison_sociale || item.nom || 'Nom inconnu',
    adresse: item.adresse || '',
    ville: item.ville || item.libelle_commune || '',
    codePostal: item.code_postal || '',
    latitude: item.latitude ? Number(item.latitude) : null,
    longitude: item.longitude ? Number(item.longitude) : null,
    siret: item.siret || '',
    siren: item.siren || '',
    activite: item.activite_principale || '',
    ca: item.chiffre_affaires ?? null,
    effectif: item.tranche_effectif_salarie || '',
    secteurHubspot: item.secteur_hubspot || '',
  };
}

function normalizeSireneItem(item) {
  return {
    id: `sirene-${item.siret || item.siren}`,
    source: 'SIRENE-LOCAL',
    nom: item.nom_raison_sociale || item.nom_commercial || item.sigle || 'Nom inconnu',
    adresse: item.adresse || '',
    ville: item.libelle_commune || '',
    codePostal: item.code_postal || '',
    latitude: item.latitude ? Number(item.latitude) : null,
    longitude: item.longitude ? Number(item.longitude) : null,
    siret: item.siret || '',
    siren: item.siren || '',
    activite: item.activite_principale || '',
    ca: item.chiffre_affaires ?? null,
    effectif: item.tranche_effectif_salarie || '',
    secteurHubspot: '',
  };
}

function normalizeAdresseItem(feature) {
  const [lng, lat] = feature.geometry.coordinates;
  return {
    id: `adresse-${feature.properties.id || feature.properties.label}`,
    source: 'ADRESSE',
    nom: feature.properties.label,
    adresse: feature.properties.label,
    ville: feature.properties.city || '',
    codePostal: feature.properties.postcode || '',
    latitude: lat,
    longitude: lng,
    siret: '',
    siren: '',
    activite: '',
    ca: null,
    effectif: '',
    secteurHubspot: '',
  };
}

const SecteursMapSearch = () => {
  const [zones, setZones] = useState([]);
  const [zonesLoading, setZonesLoading] = useState(true);
  const [zonesError, setZonesError] = useState(null);

  const [query, setQuery] = useState('');
  const [sources, setSources] = useState({
    local: true,
    sireneLocal: true,
    adresse: true,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    codePostal: '',
    departement: '',
    section: '',
    tranche: '',
    caMin: '',
    caMax: '',
  });

  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [flyTo, setFlyTo] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadZones = async () => {
      setZonesLoading(true);
      setZonesError(null);
      try {
        const data = await secteursService.getZones();
        if (!cancelled) setZones(data);
      } catch (err) {
        if (!cancelled) setZonesError(err.message || 'Erreur de chargement des zones');
      } finally {
        if (!cancelled) setZonesLoading(false);
      }
    };

    loadZones();
    return () => { cancelled = true; };
  }, []);

  const toggleSource = (key) => {
    setSources((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const clearFilters = () => {
    setFilters({
      codePostal: '',
      departement: '',
      section: '',
      tranche: '',
      caMin: '',
      caMax: '',
    });
  };

  const handleSearch = useCallback(async () => {
    const trimmed = query.trim();
    if (!trimmed) {
      setSearchError('Veuillez saisir un terme de recherche');
      return;
    }

    const activeSources = Object.entries(sources).filter(([, v]) => v).map(([k]) => k);
    if (activeSources.length === 0) {
      setSearchError('Sélectionnez au moins une source');
      return;
    }

    setSearching(true);
    setSearchError(null);
    setResults([]);
    setSelected(null);
    setFlyTo(null);

    const promises = [];

    if (sources.local) {
      promises.push(
        secteursService
          .searchExternal({ q: trimmed, source: 'local' })
          .then((data) => (data.entreprises || []).map(normalizeHubspotItem))
          .catch(() => [])
      );
    }

    if (sources.sireneLocal) {
      const params = { q: trimmed, limit: 50 };
      if (filters.codePostal) params.code_postal = filters.codePostal;
      if (filters.departement) params.departement = filters.departement;
      if (filters.section) params.section = filters.section;
      if (filters.tranche) params.tranche = filters.tranche;
      if (filters.caMin) params.ca_min = filters.caMin;
      if (filters.caMax) params.ca_max = filters.caMax;

      promises.push(
        secteursService
          .searchLocal(params)
          .then((data) => (data.entreprises || []).map(normalizeSireneItem))
          .catch(() => [])
      );
    }

    if (sources.adresse) {
      promises.push(
        geocodeAdresse(trimmed)
          .then((data) =>
            (data.features || [])
              .filter((f) =>
                isPointInAnyZone(
                  f.geometry.coordinates[1],
                  f.geometry.coordinates[0],
                  zones
                )
              )
              .map(normalizeAdresseItem)
          )
          .catch(() => [])
      );
    }

    try {
      const batches = await Promise.all(promises);
      const all = batches.flat().filter((r) => r.latitude && r.longitude);
      setResults(all);
      if (all.length === 0) {
        setSearchError('Aucun résultat trouvé');
      }
    } catch (err) {
      setSearchError(err.message || 'Erreur lors de la recherche');
    } finally {
      setSearching(false);
    }
  }, [query, sources, filters, zones]);

  const selectResult = (item) => {
    setSelected(item);
    setFlyTo({ position: [item.latitude, item.longitude], zoom: 15 });
  };

  const markers = selected
    ? [
        {
          id: selected.id,
          lat: selected.latitude,
          lng: selected.longitude,
          popup: (
            <div>
              <strong>{selected.nom}</strong>
              {selected.adresse && (
                <p className="mb-1 mt-2">
                  <small>{selected.adresse}</small>
                </p>
              )}
              {selected.codePostal && selected.ville && (
                <p className="mb-0">
                  <small>
                    {selected.codePostal} {selected.ville}
                  </small>
                </p>
              )}
            </div>
          ),
        },
      ]
    : [];

  return (
    <div className="row g-3">
      <div className="col-lg-4">
        <div className="card h-100">
          <div className="card-body d-flex flex-column">
            <h6 className="mb-3">Recherche sur la carte</h6>

            <div className="input-group mb-3">
              <input
                type="text"
                className="form-control"
                placeholder="Nom, SIRET, adresse..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSearch}
                disabled={searching}
              >
                {searching ? '...' : 'Rechercher'}
              </button>
            </div>

            <div className="mb-3">
              <p className="text-secondary-light small mb-2">Sources de recherche</p>
              <div className="d-flex flex-wrap gap-2">
                {[
                  { key: 'local', label: 'HubSpot', activeClass: 'btn-primary-600' },
                  { key: 'sireneLocal', label: 'SIRENE local', activeClass: 'btn-success-600' },
                  { key: 'adresse', label: 'Adresse (BAN)', activeClass: 'btn-danger-600' },
                ].map(({ key, label, activeClass }) => {
                  const active = sources[key];
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`btn btn-sm ${active ? activeClass : 'btn-outline-secondary'}`}
                      aria-pressed={active}
                      onClick={() => toggleSource(key)}
                    >
                      {active ? '✓ ' : ''}{label}
                    </button>
                  );
                })}
              </div>
            </div>

            {sources.sireneLocal && (
              <div className="mb-3">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary w-100"
                  onClick={() => setShowFilters((v) => !v)}
                >
                  {showFilters ? 'Masquer les filtres' : 'Filtres avancés (SIRENE)'}
                </button>
                {showFilters && (
                  <div className="mt-3 border rounded p-3">
                    <div className="row g-2">
                      <div className="col-6">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Code postal"
                          value={filters.codePostal}
                          onChange={(e) =>
                            setFilters((f) => ({ ...f, codePostal: e.target.value }))
                          }
                        />
                      </div>
                      <div className="col-6">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Département"
                          value={filters.departement}
                          onChange={(e) =>
                            setFilters((f) => ({ ...f, departement: e.target.value }))
                          }
                        />
                      </div>
                      <div className="col-6">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Section NAF"
                          value={filters.section}
                          onChange={(e) =>
                            setFilters((f) => ({ ...f, section: e.target.value }))
                          }
                        />
                      </div>
                      <div className="col-6">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Tranche effectif"
                          value={filters.tranche}
                          onChange={(e) =>
                            setFilters((f) => ({ ...f, tranche: e.target.value }))
                          }
                        />
                      </div>
                      <div className="col-6">
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          placeholder="CA min"
                          value={filters.caMin}
                          onChange={(e) =>
                            setFilters((f) => ({ ...f, caMin: e.target.value }))
                          }
                        />
                      </div>
                      <div className="col-6">
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          placeholder="CA max"
                          value={filters.caMax}
                          onChange={(e) =>
                            setFilters((f) => ({ ...f, caMax: e.target.value }))
                          }
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-link px-0 mt-2"
                      onClick={clearFilters}
                    >
                      Effacer les filtres
                    </button>
                  </div>
                )}
              </div>
            )}

            {searchError && (
              <AppAlert variant="warning" className="py-2 small mb-3">{searchError}</AppAlert>
            )}

            <div
              className="flex-grow-1 overflow-auto border rounded p-2"
              style={{ minHeight: 320, maxHeight: 520 }}
            >
              {searching && (
                <div className="text-center text-secondary-light py-4">
                  <AppSpinner size="sm" centered={false} />
                  <p className="mt-2 mb-0 small">Recherche en cours...</p>
                </div>
              )}

              {!searching && results.length === 0 && !searchError && (
                <p className="text-center text-secondary-light small mb-0 py-4">
                  Lancez une recherche pour afficher les résultats
                </p>
              )}

              {!searching &&
                results.map((item) => {
                  const badge = SOURCE_LABELS[item.source] || SOURCE_LABELS.HUBSPOT;
                  const isActive = selected?.id === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={`w-100 text-start border-0 bg-transparent p-2 rounded mb-1 ${
                        isActive ? 'bg-primary-50' : ''
                      }`}
                      onClick={() => selectResult(item)}
                    >
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <div className="small">
                          <strong>{item.nom}</strong>
                          {item.siret && (
                            <div className="text-secondary-light">SIRET : {item.siret}</div>
                          )}
                          {item.adresse && (
                            <div className="text-secondary-light">{item.adresse}</div>
                          )}
                          {item.codePostal && item.ville && (
                            <div className="text-secondary-light">
                              {item.codePostal} {item.ville}
                            </div>
                          )}
                          {item.ca != null && (
                            <div className="text-success">CA : {formatCa(item.ca)}</div>
                          )}
                        </div>
                        <span className={`badge ${badge.className} flex-shrink-0`}>
                          {badge.label}
                        </span>
                      </div>
                    </button>
                  );
                })}
            </div>
          </div>
        </div>
      </div>

      <div className="col-lg-8">
        {zonesError && (
          <AppAlert variant="warning" className="mb-3">{zonesError}</AppAlert>
        )}
        {zonesLoading ? (
          <div className="card">
            <div className="card-body text-center py-5">
              <AppSpinner centered={false} />
              <p className="mt-3 mb-0 text-secondary-light">Chargement de la carte...</p>
            </div>
          </div>
        ) : (
          <SecteursZonesMap
            zones={zones}
            markers={markers}
            flyTo={flyTo}
            height={620}
          />
        )}
        <p className="text-secondary-light small mt-2 mb-0">
          {zones.length} zone{zones.length > 1 ? 's' : ''} — cliquez un résultat pour le placer sur la carte
        </p>
      </div>
    </div>
  );
};

export default SecteursMapSearch;
